import { Button, Icon, Input, InputV2 } from '@southfields-digital/mxmz-react-ui';
import classNames from 'classnames';
import { useState, useEffect } from 'react';

import { Member, MemberTypeOptions, MemberTypes } from 'src/types/matchControl/member';

import { MEMBER_COLUMNS, NumericMemberColumnKey } from './memberColumns';

type Props = {
  member: Member;
  handleUpdateMember: (member: Member) => void;
  handleDeleteMember: () => void;
};

const MemberRow = ({ member, handleUpdateMember, handleDeleteMember }: Props) => {
  const [localMember, setLocalMember] = useState(member);
  const [numberDisplayValue, setNumberDisplayValue] = useState(member.number?.toString() || '');

  useEffect(() => {
    setLocalMember(member);
    setNumberDisplayValue(member.number?.toString() || '');
  }, [member]);

  const updateMember = (updatedMember: Member) => {
    setLocalMember(updatedMember);
    handleUpdateMember(updatedMember);
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateMember({ ...localMember, name: e.target.value });
  };

  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateMember({ ...localMember, number: e.target.value });
  };

  const handleChangeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateMember({ ...localMember, type: e.target.value as MemberTypes });
  };

  const handleChangeNumeric =
    (field: NumericMemberColumnKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
      updateMember({ ...localMember, [field]: value });
    };

  const handleNumberBlur = () => {
    // If the field is empty when user leaves it, reset to the current member number
    if (numberDisplayValue === '') {
      setNumberDisplayValue(localMember.number?.toString() || '');
    }
  };

  // Column order is driven by MEMBER_COLUMNS (the shared source of truth), so the
  // row editor and the spreadsheet paste parser always present columns identically.
  const renderColumn = (column: (typeof MEMBER_COLUMNS)[number]) => {
    switch (column.key) {
      case 'number':
        return (
          <Input
            key={column.key}
            type="text"
            value={numberDisplayValue}
            onChange={handleChangeNumber}
            onBlur={handleNumberBlur}
            placeholder="Member Number"
            className="flex-1"
          />
        );
      case 'name':
        return (
          <Input
            key={column.key}
            value={localMember.name}
            onChange={handleChangeName}
            placeholder="Member Name"
            className="flex-[2.5]"
          />
        );
      case 'type':
        return (
          <InputV2
            key={column.key}
            id="type"
            type="select"
            inputSize="lg"
            value={localMember.type}
            options={MemberTypeOptions}
            onChange={handleChangeType}
            wrapperClassName={classNames(
              'flex-1',
              // TODO: REMOVE THIS STYLING OVERRIDE WHEN ALL INPUTS USE INPUTV2
              '!min-h-[42px] !rounded-[4px] !bg-elevated-0 !border-gray-800'
            )}
          />
        );
      default:
        return (
          <Input
            key={column.key}
            type="number"
            value={localMember[column.key]?.toString() || ''}
            onChange={handleChangeNumeric(column.key)}
            placeholder={column.label}
            min={0}
            max={column.key === 'age' ? 99 : undefined}
            className="flex-1"
          />
        );
    }
  };

  return (
    <div className="flex flex-row gap-2 items-center px-2 py-1">
      <Icon icon="sort" weight="bold" className="text-[#5B5D70]" />
      {MEMBER_COLUMNS.map(renderColumn)}
      <Button variant="danger" onClick={handleDeleteMember}>
        <Icon icon="trash" />
      </Button>
    </div>
  );
};

export default MemberRow;
